import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  Paper,
  makeStyles,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  CircularProgress,
  LinearProgress,
  Tooltip,
  withStyles
} from "@material-ui/core";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import CodeRoundedIcon from '@material-ui/icons/CodeRounded';
import PeopleAltRoundedIcon from '@material-ui/icons/PeopleAltRounded';
import LocalShippingRoundedIcon from '@material-ui/icons/LocalShippingRounded';
import PersonAddRoundedIcon from '@material-ui/icons/PersonAddRounded';
import MailOutlineRoundedIcon from '@material-ui/icons/MailOutlineRounded';
import MoreHorizRoundedIcon from '@material-ui/icons/MoreHorizRounded';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import EditRoundedIcon from '@material-ui/icons/EditRounded';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TECH_LIKE_CATEGORIES = ['Tech Related Query'];

const NAVY = "#1C375D";
const ORANGE = "#F47E46";
const MUTED = "#6B7A94";
const BORDER = "#E3E8F0";

// `value` is what gets STORED on the ticket and queried in Mongo
// (categoryDetails.category, see ticketDashBoard.js) - renaming it would
// orphan every ticket already raised. Only `label` is what people read, so
// the two are kept apart deliberately.
//
// `hidden` keeps a category out of the picker without deleting it: tickets
// already raised under it still render their own label elsewhere.
// `tone` is fg / tint / border. Every fg clears AA on BOTH white and its own
// tint (4.77 at worst), so the icon stays readable whether the card is picked
// or not.
const CATEGORY_OPTIONS = [
  { value: 'Tech Related Query', label: 'ERP Tech Support Tickets', hint: 'ERP, app or tooling issue', Icon: CodeRoundedIcon , tone: { fg: '#5B3FA8', bg: '#F3EEFC', bd: '#D9CCF5' } },
  { value: 'Others', label: 'Other ERP Tickets', hint: 'Anything else', Icon: MoreHorizRoundedIcon , tone: { fg: '#55606B', bg: '#F1F3F5', bd: '#DCE1E6' } },
  { value: 'Customers', label: 'Customers', hint: 'Linked to a project', Icon: PeopleAltRoundedIcon , tone: { fg: '#1F5FB8', bg: '#EEF3FC', bd: '#CBDCF6' } },
  { value: 'Vendors', label: 'Vendors', hint: 'Supplier or partner', Icon: LocalShippingRoundedIcon , tone: { fg: '#13697A', bg: '#E8F4F6', bd: '#C3E2E8' } },
  { value: 'Job Applicant', label: 'Job Applicant', hint: 'Hiring related', Icon: PersonAddRoundedIcon , tone: { fg: '#1F7A4D', bg: '#E8F6EE', bd: '#C4E7D4' } },
  { value: 'Trial Email', label: 'Trial Email', hint: 'Test the mail flow', Icon: MailOutlineRoundedIcon, hidden: true , tone: { fg: '#8A6100', bg: '#FEF3D7', bd: '#F2DFA8' } },
];

// Today, from LOCAL parts. toISOString() is UTC, so in IST (+05:30) it
// returns yesterday's date between midnight and 05:30 - and the picker would
// quietly offer a day that is already past.
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const VISIBLE_CATEGORIES = CATEGORY_OPTIONS.filter((c) => !c.hidden);
const categoryLabel = (value) =>
  (CATEGORY_OPTIONS.find((c) => c.value === value) || {}).label || value;

// The one department that must not appear in the assignee picker - tech work
// belongs in "ERP Tech Support Tickets", which routes through the Escalation
// Matrix instead.
const EXCLUDED_DEPARTMENT = 'Tech';

// First letters of the first two words - "Anil Kumar Verma" is AK, not AKV.
const initialsOf = (name) => String(name || '')
  .trim().split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toUpperCase()).join('') || '?';

const BrandLinearProgress = withStyles(() => ({
  root: { height: 3, backgroundColor: "rgba(244,126,70,0.16)" },
  bar: { backgroundColor: ORANGE },
}))(LinearProgress);

const STEPS = [
  { no: 1, title: 'Context', hint: 'Who is this for' },
  { no: 2, title: 'Details', hint: 'Title & description' },
  { no: 3, title: 'Assign', hint: 'Owner & due date' },
];

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: 900,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // MUI's own dialog paper is `display:flex; flex-direction:column;
    // max-height: calc(100% - 64px)`, so this Paper is a flex ITEM in it: at
    // high zoom it SHRINKS to fit instead of overflowing, and the
    // `overflow: hidden` above then clips the rest with no scrollbar. Capping
    // it here means the shrink lands on `body`, which can scroll.
    //
    // Deliberately no min-height: min-height beats max-height in CSS, so one
    // here would force the shell past the cap at exactly the zoom levels this
    // is meant to fix.
    maxHeight: "calc(100vh - 64px)",
  },
  header: {
    background: NAVY,
    padding: "22px 32px 18px 32px",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    flexShrink: 0,
  },
  headerTitle: { fontSize: 20, fontWeight: 700, color: "#FFFFFF" },
  stepRow: { display: "flex", alignItems: "center" },
  stepDot: {
    width: 34, height: 34, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, fontWeight: 700, flexShrink: 0,
  },
  stepConnector: { flexGrow: 1, height: 2, background: "#3A5378", margin: "0 16px" },
  // The one scrolling region. minHeight:0 is what lets a flex item shrink
  // below its content; without it the browser refuses and the clip comes back.
  body: {
    padding: "26px 32px", flex: "1 1 auto", minHeight: 0,
    overflowY: "auto", overscrollBehavior: "contain",
  },
  // The step layout, and the old minHeight. It sits on the CONTENT rather than
  // on the scroll box, so it still stops the shell jumping between steps
  // without blocking the scroll.
  bodyInner: { display: "flex", flexDirection: "column", gap: 20, minHeight: 380 },
  sectionTitle: { fontSize: 19, fontWeight: 700, color: NAVY, margin: 0 },
  sectionHint: { fontSize: 13, color: MUTED, margin: "4px 0 0 0" },
  catGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 },
  catCard: {
    position: "relative",
    textAlign: "left", padding: 14, borderRadius: 14, cursor: "pointer",
    background: "#FFFFFF", border: `1.5px solid ${BORDER}`,
    display: "flex", flexDirection: "column", gap: 6, font: "inherit", minHeight: 44,
    transition: "border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease",
  },
  catIconWrap: {
    width: 34, height: 34, borderRadius: 10, marginBottom: 2,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  catTick: { position: "absolute", top: 10, right: 11, display: "flex" },
  catLabel: { fontSize: 14, fontWeight: 700, color: NAVY, lineHeight: 1.25 },
  // #5D6B82, not MUTED: the old #6B7A94 measures 4.34 on plain white and 3.81
  // on the lightest tint - under AA for 12px either way.
  catHint: { fontSize: 12, color: "#5D6B82" },
  label: { fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6, display: "block" },
  helper: { fontSize: 12, color: MUTED, marginTop: 6, display: "block" },
  infoBar: {
    display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
    background: "#F6F8FC", border: `1px solid ${BORDER}`, borderRadius: 12,
    fontSize: 13, color: "#44546F",
  },
  quillBox: {
    border: "1px solid #D7DEEA", borderRadius: 12, overflow: "hidden", background: "#FFFFFF",
    "& .ql-toolbar": { border: "none", borderBottom: `1px solid ${BORDER}`, background: "#F6F8FC" },
    "& .ql-container": { border: "none", minHeight: 170, maxHeight: 300, overflow: "auto", fontFamily: "inherit", fontSize: 14 },
  },
  fileBtn: {
    minHeight: 44, padding: "0 16px", borderRadius: 10, border: "1px solid #C9D3E4",
    background: "#FFFFFF", display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 700, color: NAVY,
  },
  fileChip: {
    minHeight: 44, padding: "0 14px", borderRadius: 10, border: `1px solid ${BORDER}`,
    background: "#F6F8FC", display: "inline-flex", alignItems: "center", gap: 9,
    fontSize: 13, fontWeight: 600, color: NAVY,
  },
  ownerCard: {
    display: "flex", alignItems: "center", gap: 14, padding: "14px 16px",
    background: "#F6F8FC", border: `1px solid ${BORDER}`, borderRadius: 14,
  },
  ownerAvatar: {
    width: 44, height: 44, borderRadius: "50%", background: "#E4ECFA", color: NAVY,
    fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  tatPill: { padding: "6px 14px", borderRadius: 999, background: "#FEF3D7", color: "#8A6100", fontSize: 12, fontWeight: 700 },
  reviewBox: { border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden", background: "#FFFFFF" },
  reviewHead: {
    padding: "12px 18px", background: "#F6F8FC", borderBottom: `1px solid ${BORDER}`,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: MUTED,
  },
  titleCard: {
    display: "flex", alignItems: "center", gap: 13, padding: "15px 17px",
    borderRadius: 12, background: NAVY,
  },
  titleCardIcon: {
    width: 36, height: 36, borderRadius: 10, background: ORANGE,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#FFFFFF",
  },
  tileGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 },
  tile: { padding: "13px 14px", borderRadius: 12, background: "#F6F8FC", display: "flex", flexDirection: "column", gap: 7 },
  tileLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: MUTED },
  tileValue: { fontSize: 13, fontWeight: 700, color: NAVY, wordBreak: "break-word" },
  footer: {
    padding: "16px 32px", borderTop: `1px solid ${BORDER}`,
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    flexShrink: 0, background: "#FFFFFF",
  },
  ghostBtn: {
    minHeight: 44, padding: "0 22px", borderRadius: 10, border: "1px solid #C9D3E4",
    background: "#FFFFFF", color: NAVY, fontSize: 14, fontWeight: 600, textTransform: "none",
  },
  primaryBtn: {
    minHeight: 44, padding: "0 26px", borderRadius: 10, background: ORANGE, color: "#FFFFFF",
    fontSize: 14, fontWeight: 700, textTransform: "none",
    "&:hover": { background: "#E0663D" },
  },
  pickerStack: { display: "flex", flexDirection: "column", gap: 16 },
  pickerStep: { display: "flex", gap: 12, alignItems: "flex-start" },
  pickerBadge: {
    width: 22, height: 22, borderRadius: "50%", flexShrink: 0, marginTop: 26,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800, color: "#FFFFFF",
  },
  userRow: { display: "flex", alignItems: "center", gap: 11, minWidth: 0, padding: "2px 0" },
  userAvatar: {
    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800, background: "#E4ECFA", color: NAVY,
  },
  userName: { fontSize: 13.5, fontWeight: 700, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  userMeta: { fontSize: 11.5, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  userRole: { fontSize: 11, fontWeight: 600, color: "#2F7BEF", marginTop: 1 },
  qtValue: { display: "flex", alignItems: "center", gap: 9, minWidth: 0 },
  qtValueText: { fontWeight: 600, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  qtOwnerChip: {
    flexShrink: 0, fontSize: 11, fontWeight: 700, color: "#1F6F4A",
    background: "#E6F4EC", borderRadius: 999, padding: "1px 9px",
  },
  qtNoOwnerChip: {
    flexShrink: 0, fontSize: 11, fontWeight: 700, color: "#B3261E",
    background: "#FBE9E7", borderRadius: 999, padding: "1px 9px",
  },
  qtMenuPaper: { maxHeight: 380, minWidth: 420, borderRadius: 12, marginTop: 4 },
  qtItem: { display: "flex", alignItems: "center", gap: 12, paddingTop: 9, paddingBottom: 9 },
  qtAvatar: {
    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800, background: "#E4ECFA", color: NAVY,
  },
  qtItemMain: { display: "flex", flexDirection: "column", minWidth: 0, gap: 1 },
  qtItemText: { fontSize: 13.5, fontWeight: 600, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  qtItemOwner: { fontSize: 11.5, color: MUTED },
  qtItemNoOwner: { fontSize: 11.5, fontWeight: 600, color: "#B3261E" },
  footerNote: { minWidth: 0, flex: 1 },
  blockerNote: {
    display: "inline-flex", alignItems: "center", gap: 7,
    fontSize: 12.5, fontWeight: 600, color: "#9A5B00",
  },
  searchContainer: { display: "flex", alignItems: "center", position: "relative", width: "100%" },
  searchResults: {
    position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 300, overflowY: "auto",
    backgroundColor: "white", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", zIndex: 1000,
    borderRadius: 4, marginTop: 5,
  },
  searchResultItem: { cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } },
}));

const CreateTaskTicket = (props) => {

  const { visibilityFunction, refreshParentDataNo, refreshParentData } = props;

  const classes = useStyles();

  const [activeStep, setActiveStep] = useState(1);

  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketETAdate, setTicketETAdate] = useState('');
  const [wrongEmail, setWrongEmail] = useState(false);
  const [files, setFiles] = useState([""]);
  const [userEmail, setUserEmail] = useState("");
  const [wrongMails, setWrongMails] = useState("");
  const [uploadingIndex, setUploadingIndex] = useState(null);
  // Local, not props.setLoading: the global loader blanks the whole screen,
  // which on a 3-step wizard means losing everything the user just typed from
  // view at the exact moment they want to see it confirmed.
  const [submitting, setSubmitting] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // ---- assignee picker (every category except ERP Tech Support) ----------
  // One fetch of active users, with department / city / centre / roles already
  // populated, and the whole cascade filters that in memory. A request per
  // step would make each dropdown wait on the network for data the first
  // request already carried.
  const [allUsers, setAllUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allCentres, setAllCentres] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [pickedDepartment, setPickedDepartment] = useState(null);
  const [pickedCity, setPickedCity] = useState(null);
  const [pickedCentre, setPickedCentre] = useState(null);
  const [pickedUsers, setPickedUsers] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [projectNo, setProjectNo] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [trialEmailOthers, setTrialEmailOthers] = useState('');
  const [escalationCategories, setEscalationCategories] = useState([]);
  const [selectedTechCategory, setSelectedTechCategory] = useState('');

  const [projectSearchValue, setProjectSearchValue] = useState('');
  const [projectSearchResults, setProjectSearchResults] = useState([]);
  const [showProjectResults, setShowProjectResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchAnchorRef = useRef(null);
  const bodyRef = useRef(null);

  // The body scrolls now, so without this a step opens at the previous step's
  // scroll offset - part-way down.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeStep]);

  const isTech = TECH_LIKE_CATEGORIES.includes(selectedCategory);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'align',
  ];

  const isDescriptionEmpty = () =>
    !ticketDescription || !ticketDescription.replace(/<(.|\n)*?>/g, '').trim();

  const handleTickeTitleInputChange = (e) => {
    setTicketTitle(e.target.value)
  }

  const handleTicketDescriptionChange = (content) => {
    setTicketDescription(content);
  }

  const handleETAdateInputChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0)

    if (selectedDate >= currentDate) {
      setTicketETAdate(e.target.value);
    } else {
      props.setSnackInfo("Please select a date in the present or future.", 'warning');
    }
  };

  const onImageChangeCheck = async (e, index) => {
    if (!e.target.files || !e.target.files.length) return;
    const newFiles = [...files];

    setUploadingIndex(index);
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    try {
      const fileUploadRes = await HttpService.uploadFile(fd);
      newFiles[index] = fileUploadRes.data;
      setFiles(newFiles);
    } catch (err) {
      props.setSnackInfo(err?.response?.data ?? "Something went wrong", "error");
    }
    setUploadingIndex(null);
    e.target.value = null;
  }

  const handleAddFileInput = () => {
    let newFiles = [...files];
    newFiles.push("");
    setFiles(newFiles);
  };

  const handleRemoveFileInput = (index) => {
    const newFiles = files.filter((file, id) => id !== index);
    setFiles(newFiles.length ? [...newFiles] : [""]);
  }

  const searchProjects = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setProjectSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await HttpService.searchProjectsByNumber(searchValue);
      if (response.data && response.data.data) {
        setProjectSearchResults(response.data.data);
        setShowProjectResults(true);
      }
    } catch (error) {
      console.error('Error searching projects:', error);
      props.setSnackInfo('Error searching projects', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // Loaded once, the first time a non-tech category is picked - no point
  // pulling the whole directory for a ticket that routes itself.
  const loadDirectory = async () => {
    if (allUsers.length || directoryLoading) return;
    setDirectoryLoading(true);
    const [userRes, deptRes, ecRes] = await Promise.allSettled([
      HttpService.getAllUsersforAcess({ isActive: true }),
      HttpService.getDepartments(),
      // Needed for its locationId: a centre's city is a property of the
      // centre, not of whoever happens to work there.
      HttpService.getExperinceCenter(),
    ]);
    if (userRes.status === 'fulfilled') {
      setAllUsers(Array.isArray(userRes.value?.data) ? userRes.value.data : []);
    } else {
      props.setSnackInfo('Could not load the user directory', 'error');
    }
    if (deptRes.status === 'fulfilled') {
      const list = Array.isArray(deptRes.value?.data) ? deptRes.value.data : [];
      // Tech is excluded here, not hidden later, so it can never be picked.
      setDepartments(list.filter((d) => d?.name !== EXCLUDED_DEPARTMENT));
    } else {
      props.setSnackInfo('Could not load the department list', 'error');
    }
    if (ecRes.status === 'fulfilled') {
      setAllCentres(Array.isArray(ecRes.value?.data) ? ecRes.value.data : []);
    } else {
      props.setSnackInfo('Could not load the experience centre list', 'error');
    }
    setDirectoryLoading(false);
  };

  useEffect(() => {
    const fetchEscalationCategories = async () => {
      setCategoriesLoading(true);
      try {
        const res = await HttpService.getEscalationMatrixList();
        setEscalationCategories(res?.data?.data || []);
      } catch (error) {
        console.error('Error fetching escalation matrix categories:', error);
        props.setSnackInfo('Could not load the query types', 'error');
      }
      setCategoriesLoading(false);
    };
    fetchEscalationCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (projectSearchValue && projectSearchValue.length >= 2) {
        searchProjects(projectSearchValue);
      } else {
        setProjectSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [projectSearchValue]);

  const handleProjectSearchChange = (e) => {
    setProjectSearchValue(e.target.value);
    if (e.target.value.length < 2) {
      setShowProjectResults(false);
    }
  };

  const handleProjectSelect = (project) => {
    setProjectNo(project.erpProjectNo || project.projectNumber);
    setCustomerName(project.customerName || project.customerDetails?.customerName || '');
    setProjectSearchValue('');
    setProjectSearchResults([]);
    setShowProjectResults(false);
  };

  const handleClearProjectSearch = () => {
    setProjectSearchValue('');
    setProjectSearchResults([]);
    setShowProjectResults(false);
  };

  const handleClickAway = () => {
    setShowProjectResults(false);
  };

  const handleSubmitButton = async (e) => {
    try {
      setSubmitting(true);

      let categoryData = {};
      if (selectedCategory) {
        categoryData.category = selectedCategory;
        switch (selectedCategory) {
          case 'Customers':
            categoryData.projectNo = projectNo;
            categoryData.customerName = customerName;
            break;
          case 'Vendors':
            categoryData.vendorName = vendorName;
            categoryData.vendorEmail = vendorEmail;
            break;
          case 'Job Applicant':
            categoryData.applicantName = applicantName;
            categoryData.applicantEmail = applicantEmail;
            break;
          case 'Trial Email':
            categoryData.others = trialEmailOthers;
            break;
          case 'Tech Related Query':
            categoryData.queryType = selectedTechCategory;
            break;
          default:
            break;
        }
      }

      let obj = {
        ticketTitle: ticketTitle,
        ticketDescription: ticketDescription,
        ticketETAdate: ticketETAdate,
        uploadedFiles: files,
        email: userEmail,
        ...(selectedCategory && { categoryDetails: categoryData })
      };

      const response = await HttpService.createTask(obj);

      if (response.status === 200) {
        props.setSnackInfo('Internal Ticket Created Successfully', "success");
        refreshParentDataNo();
        refreshParentData();

        setTicketTitle('');
        setTicketDescription('');
        setTicketETAdate('');
        setFiles([""]);
        setUserEmail('');
        setSelectedCategory('');
        setProjectNo('');
        setCustomerName('');
        setVendorName('');
        setVendorEmail('');
        setApplicantName('');
        setApplicantEmail('');
        setTrialEmailOthers('');
        setProjectSearchValue('');
        setProjectSearchResults([]);
        setSelectedTechCategory('');
        resetPicker();
        setWrongEmail(false);
        setWrongMails('');
        setUploadingIndex(null);
        setActiveStep(1);
        visibilityFunction(false);
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) {
        const data = error.response.data;
        const wrongEmailList = data?.wrongEmails
          ? data.wrongEmails.join(', ')
          : (Array.isArray(data) ? data.join(', ') : 'Unknown email');

        props.setSnackInfo(
          `User(s) not found or inactive: ${wrongEmailList}`,
          "error"
        );
        setWrongMails(wrongEmailList);
        setWrongEmail(true);
      } else {
        props.setSnackInfo(
          error?.response?.data?.message ?? "Something went wrong",
          "error"
        );
        setWrongEmail(false);
        setWrongMails('');
      }
    } finally {
      setSubmitting(false);
    }
  };


  const handleTechCategoryChange = (e) => {
    const queryType = e.target.value;
    setSelectedTechCategory(queryType);
    const matchedEntry = escalationCategories.find((c) => c.text === queryType);
    const l1Email = matchedEntry?.l1User?.email;
    setUserEmail(l1Email ? l1Email.toLowerCase() : '');
    if (!l1Email) {
      props.setSnackInfo('No L1 user configured for this query type', 'warning');
    }
  }

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    resetPicker();
    if (value && !TECH_LIKE_CATEGORIES.includes(value)) loadDirectory();
    setProjectNo('');
    setCustomerName('');
    setVendorName('');
    setVendorEmail('');
    setApplicantName('');
    setApplicantEmail('');
    setTrialEmailOthers('');
    setProjectSearchValue('');
    setProjectSearchResults([]);
    setSelectedTechCategory('');
    setUserEmail('');
    setTicketETAdate('');
  };

  // ---- the cascade -------------------------------------------------------
  // locationId and experienceCenterId are ARRAYS on a user - somebody can
  // cover two cities - so membership tests are `some`, not equality.
  const idOf = (v) => String((v && v._id) || v || '');
  const inList = (list, id) => Array.isArray(list) && list.some((x) => idOf(x) === String(id));

  const usersInDepartment = pickedDepartment
    ? allUsers.filter((u) => idOf(u.departmentId) === idOf(pickedDepartment))
    : [];

  // A centre's city comes from the CENTRE's own locationId, never from the
  // people in it. Deriving it from users let somebody who covers two cities
  // drag their other city's centres into this one's list - pick Bangalore and
  // a Hyderabad centre would appear, and picking it would then "work".
  const departmentCentres = pickedDepartment
    ? allCentres.filter((c) => usersInDepartment.some((u) => inList(u.experienceCenterId, c._id)))
    : [];

  const cityOptions = (() => {
    const seen = new Map();
    departmentCentres.forEach((c) => {
      const loc = c.locationId;
      if (loc && loc._id && !seen.has(String(loc._id))) seen.set(String(loc._id), loc);
    });
    return Array.from(seen.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  })();

  const centreOptions = pickedCity
    ? departmentCentres
      .filter((c) => idOf(c.locationId) === idOf(pickedCity))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    : [];

  // The centre is already pinned to the chosen city, so covering it is the
  // only test left.
  const userOptions = pickedCentre
    ? usersInDepartment
      .filter((u) => inList(u.experienceCenterId, idOf(pickedCentre)))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    : [];

  const roleNamesOf = (u) => (u?.roles || []).map((r) => r?.name).filter(Boolean).join(', ');

  // The payload contract is unchanged - the server still receives `email` as a
  // comma-separated string. Only the way it is assembled has changed.
  const applyPickedUsers = (picked) => {
    setPickedUsers(picked);
    setUserEmail(picked.map((u) => String(u.email || '').toLowerCase()).filter(Boolean).join(', '));
  };

  // Cleared deepest-first on purpose. React 17 does not batch setState calls
  // made after an await, so each one gets its own render; clearing the
  // department first left a frame where pickedCity was still set and
  // pickedDepartment was already null, and the city block read .name off it.
  const resetPicker = () => {
    applyPickedUsers([]);
    setPickedCentre(null);
    setPickedCity(null);
    setPickedDepartment(null);
  };

  const resetCascade = (level) => {
    if (level <= 1) { setPickedCity(null); }
    if (level <= 2) { setPickedCentre(null); }
    applyPickedUsers([]);
  };

  const matchedEscalation = escalationCategories.find((c) => c.text === selectedTechCategory);
  const l1Name = matchedEscalation?.l1User?.name || '';

  const goNext = () => {
    if (uploadingIndex !== null) {
      props.setSnackInfo('Please wait, file is still uploading', 'warning');
      return;
    }
    if (activeStep === 1) {
      if (isTech && !selectedTechCategory) {
        props.setSnackInfo('Please select a query type', 'warning');
        return;
      }
      setActiveStep(2);
      return;
    }
    if (activeStep === 2) {
      if (ticketTitle.trim() === '') {
        props.setSnackInfo('Please enter a title', 'warning');
        return;
      }
      if (isDescriptionEmpty()) {
        props.setSnackInfo('Please enter a description', 'warning');
        return;
      }
      setActiveStep(3);
    }
  };

  const goBack = () => setActiveStep((s) => (s > 1 ? s - 1 : s));

  // Returns the REASON, not a bare boolean. A greyed-out Create button with
  // nothing beside it is the thing people report as "the page is broken" -
  // and on step 3 the blocker is usually on step 2, out of sight.
  const submitBlocker = (() => {
    if (uploadingIndex !== null) return 'An attachment is still uploading.';
    if (ticketTitle.trim() === '') return 'A title is needed — go back to Details.';
    if (isDescriptionEmpty()) return 'A description is needed — go back to Details.';
    if (!isTech && !ticketETAdate) return 'Pick a due date.';
    if (!userEmail) {
      if (isTech) return 'No L1 owner is set for this query type — pick a different one.';
      if (!pickedDepartment) return 'Pick a department to find the owner.';
      if (!pickedCity) return 'Pick a city.';
      if (!pickedCentre) return 'Pick an experience center.';
      return 'Pick at least one person to assign this to.';
    }
    return '';
  })();
  const submitDisabled = Boolean(submitBlocker) || submitting;

  const renderSubFields = () => {
    switch (selectedCategory) {
      case 'Customers':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={classes.searchContainer} ref={searchAnchorRef}>
                <TextField
                  id="projectSearch"
                  label="Search Project No. / Lead Number / Unique Lead Number"
                  value={projectSearchValue}
                  onChange={handleProjectSearchChange}
                  onFocus={() => projectSearchResults.length > 0 && setShowProjectResults(true)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {isSearching ? (
                          <CircularProgress size={16} thickness={5} style={{ color: ORANGE }} />
                        ) : projectSearchValue ? (
                          <IconButton size="small" onClick={handleClearProjectSearch}>
                            <CloseIcon />
                          </IconButton>
                        ) : (
                          <SearchIcon color="action" />
                        )}
                      </InputAdornment>
                    )
                  }}
                  placeholder="Enter project no. / Lead Number / Unique Lead Number to search"
                />

                {showProjectResults && (projectSearchResults.length > 0 || (projectSearchValue.length >= 2 && !isSearching)) && (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper className={classes.searchResults}>
                      <List component="nav" dense>
                        {projectSearchResults.length > 0 ? (
                          projectSearchResults.map((project, index) => (
                            <ListItem
                              key={project._id || index}
                              button
                              onClick={() => handleProjectSelect(project)}
                              className={classes.searchResultItem}
                            >
                              <ListItemText
                                primary={`${project.erpProjectNo || project.projectNumber}`}
                                secondary={
                                  <>
                                    <span>Customer: {project.customerName || 'N/A'}</span>
                                    {project.uniqueLeadNumber && (
                                      <span> | Lead: {project.uniqueLeadNumber}</span>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText primary="No projects found" />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  </ClickAwayListener>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="projectNo"
                label="Project No."
                value={projectNo}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="customerName"
                label="Customer Name"
                value={customerName}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>
          </Grid>
        );

      case 'Vendors':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="vendorName" label="Vendor Name" value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="vendorEmail" label="Vendor Email" type="email" value={vendorEmail}
                onChange={(e) => setVendorEmail(e.target.value)}
                variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        );

      case 'Job Applicant':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="applicantName" label="Applicant Name" value={applicantName}
                onChange={(e) => setApplicantName(e.target.value)}
                variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="applicantEmail" label="Applicant Email" type="email" value={applicantEmail}
                onChange={(e) => setApplicantEmail(e.target.value)}
                variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        );

      case 'Trial Email':
        return (
          <TextField
            id="trialEmailOthers" label="Others" value={trialEmailOthers}
            onChange={(e) => setTrialEmailOthers(e.target.value)}
            variant="outlined" size="small" fullWidth multiline rows={2}
            InputLabelProps={{ shrink: true }} placeholder="Enter other details"
          />
        );

      case 'Tech Related Query':
        return (
          <div style={{ maxWidth: 520 }}>
            <FormControl variant="outlined" size="small" fullWidth disabled={categoriesLoading}>
              <InputLabel>Query Type *</InputLabel>
              <Select
                label="Query Type *"
                value={selectedTechCategory}
                onChange={handleTechCategoryChange}
                // Without this the list rendered empty while the Escalation
                // Matrix was still loading, which reads as "there are no query
                // types" rather than "not here yet".
                startAdornment={categoriesLoading ? (
                  <InputAdornment position="start">
                    <CircularProgress size={14} thickness={5} style={{ color: ORANGE }} />
                  </InputAdornment>
                ) : null}
                // The closed field showed the bare type name and nothing else,
                // so the one thing the choice actually DOES - assign an owner -
                // was invisible until you reached step 3.
                renderValue={(value) => {
                  const entry = escalationCategories.find((c) => c.text === value);
                  const owner = entry?.l1User?.name;
                  return (
                    <span className={classes.qtValue}>
                      <span className={classes.qtValueText}>{value}</span>
                      {owner
                        ? <span className={classes.qtOwnerChip}>{owner}</span>
                        : <span className={classes.qtNoOwnerChip}>No L1 owner</span>}
                    </span>
                  );
                }}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  PaperProps: { className: classes.qtMenuPaper },
                }}
              >
                {escalationCategories.map((c) => {
                  const owner = c?.l1User?.name;
                  return (
                    <MenuItem key={c._id} value={c.text} className={classes.qtItem}>
                      <span className={classes.qtAvatar} style={owner ? undefined : { background: "#FBE9E7", color: "#B3261E" }}>
                        {owner ? initialsOf(owner) : '!'}
                      </span>
                      <span className={classes.qtItemMain}>
                        <span className={classes.qtItemText}>{c.text}</span>
                        {/* Named here rather than left to a toast after the
                            fact: picking a type IS picking the owner, and a
                            type with none configured is a dead end - submit
                            stays blocked on it. */}
                        <span className={owner ? classes.qtItemOwner : classes.qtItemNoOwner}>
                          {owner ? `L1 · ${owner}` : 'No L1 owner configured — cannot be submitted'}
                        </span>
                      </span>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <span className={classes.helper}>
              {categoriesLoading
                ? 'Loading query types from the Escalation Matrix…'
                : escalationCategories.length
                  ? `${escalationCategories.length} query types · picking one assigns its L1 owner and starts the TAT clock`
                  : 'No query types are configured in the Escalation Matrix yet.'}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const renderStepper = () => (
    <div className={classes.stepRow}>
      {STEPS.map((s, i) => {
        const done = activeStep > s.no;
        const active = activeStep === s.no;
        return (
          <Fragment key={s.no}>
            {i > 0 && <span className={classes.stepConnector} />}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                className={classes.stepDot}
                style={
                  done
                    ? { background: "#2E8B67", color: "#FFFFFF" }
                    : active
                      ? { background: ORANGE, color: "#FFFFFF" }
                      : { background: "transparent", border: "1.5px solid #6D83A6", color: "#A9BBD4" }
                }
              >
                {done ? <CheckRoundedIcon style={{ fontSize: 18 }} /> : s.no}
              </span>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: 14, fontWeight: active ? 700 : 600, color: done ? "#CFE3D8" : active ? "#FFFFFF" : "#A9BBD4" }}>
                  {s.title}
                </span>
                <span style={{ fontSize: 11, color: done ? "#8FB8A4" : active ? "#A9BBD4" : "#6D83A6" }}>
                  {s.hint}
                </span>
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );

  const renderStepOne = () => (
    <>
      <div>
        <p className={classes.sectionTitle}>What is this ticket about?</p>
        <p className={classes.sectionHint}>Pick a category so the ticket reaches the right team.</p>
      </div>

      <div className={classes.catGrid}>
        {VISIBLE_CATEGORIES.map(({ value, label, hint, Icon, tone }) => {
          const active = selectedCategory === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => handleCategoryChange(value)}
              className={classes.catCard}
              style={active
                ? { background: tone.bg, borderColor: tone.fg, boxShadow: `0 2px 10px ${tone.fg}22` }
                : { borderColor: tone.bd }}
            >
              {/* The icon keeps its tint whether the card is picked or not, so
                  the categories stay distinguishable at a glance rather than
                  only once one has been chosen. */}
              <span className={classes.catIconWrap} style={{ background: tone.bg, color: tone.fg }}>
                <Icon style={{ fontSize: 19 }} />
              </span>
              <span className={classes.catLabel} style={active ? { color: tone.fg } : undefined}>{label}</span>
              <span className={classes.catHint}>{hint}</span>
              {active && (
                <span className={classes.catTick} style={{ color: tone.fg }}>
                  <CheckRoundedIcon style={{ fontSize: 15 }} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {renderSubFields()}

      {isTech && (
        <div className={classes.infoBar}>
          <InfoOutlinedIcon style={{ fontSize: 18, color: "#2F7BEF" }} />
          <span>Choosing a query type auto-assigns the L1 owner and starts the TAT clock.</span>
        </div>
      )}
    </>
  );

  const renderStepTwo = () => (
    <>
      <div>
        <p className={classes.sectionTitle}>Describe the issue</p>
        <p className={classes.sectionHint}>A clear title and steps help the owner resolve it faster.</p>
      </div>

      <div>
        <label htmlFor="ticketTitle" className={classes.label}>Title *</label>
        <TextField
          id="ticketTitle"
          value={ticketTitle}
          onChange={handleTickeTitleInputChange}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Short summary of the issue"
        />
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <span className={classes.label}>Description *</span>
          <span style={{ fontSize: 12, color: MUTED }}>Rich text — lists, headings and emphasis</span>
        </div>
        <div className={classes.quillBox}>
          <ReactQuill
            theme="snow"
            value={ticketDescription}
            onChange={handleTicketDescriptionChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="What happened, what you expected, and any steps to reproduce."
          />
        </div>
      </div>

      <div>
        <span className={classes.label}>Attachments</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {files.map((file, index) => (
            <Fragment key={`file-upload-${index}`}>
              <input
                accept="*"
                id={`contained-button-file-${index}`}
                name="chmImageAttachment"
                onChange={(e) => onImageChangeCheck(e, index)}
                type="file"
                style={{ display: "none" }}
              />

              {uploadingIndex === index ? (
                <span className={classes.fileChip} style={{ color: MUTED }}>
                  <CircularProgress size={16} thickness={5} style={{ color: ORANGE }} />
                  Uploading…
                </span>
              ) : file === "" ? (
                <label
                  htmlFor={`contained-button-file-${index}`}
                  className={classes.fileBtn}
                  style={uploadingIndex !== null ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                >
                  <AddRoundedIcon style={{ fontSize: 18, color: ORANGE }} />
                  Add file
                </label>
              ) : (
                <span className={classes.fileChip}>
                  <CheckCircleIcon style={{ fontSize: 17, color: "#2E8B67" }} />
                  Attachment {index + 1}
                  <IconButton
                    size="small"
                    aria-label="Remove attachment"
                    onClick={() => handleRemoveFileInput(index)}
                  >
                    <CloseIcon style={{ fontSize: 15, color: "#B3261E" }} />
                  </IconButton>
                </span>
              )}
            </Fragment>
          ))}

          {files.every((f) => f !== "") && (
            <Button
              className={classes.ghostBtn}
              onClick={handleAddFileInput}
              startIcon={<AddRoundedIcon />}
              disabled={uploadingIndex !== null}
            >
              Add another
            </Button>
          )}
        </div>
      </div>
    </>
  );

  const renderStepThree = () => (
    <>
      <div>
        <p className={classes.sectionTitle}>Who owns this, and by when?</p>
        <p className={classes.sectionHint}>Tech queries are routed automatically; other categories need an email.</p>
      </div>

      {isTech ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <label htmlFor="tagUser" className={classes.label}>Assign to *</label>
            <TextField
              id="tagUser"
              value={userEmail}
              variant="outlined"
              size="small"
              fullWidth
              disabled
            />
            <span className={classes.helper}>
              Locked — filled from the Escalation Matrix for this query type.
            </span>
          </Grid>
        </Grid>
      ) : (
        <>
          {/* Four dependent steps, each appearing only once the one above it
              is answered. Showing all four at once invites people to fill the
              bottom one first, and every option in it would be wrong. */}
          <div className={classes.pickerStack}>
            <div className={classes.pickerStep}>
              <span className={classes.pickerBadge} style={{ background: ORANGE }}>1</span>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <label className={classes.label}>Department *</label>
                <Autocomplete
                  size="small"
                  disabled={directoryLoading}
                  loading={directoryLoading}
                  options={departments}
                  value={pickedDepartment}
                  onChange={(e, v) => { setPickedDepartment(v); resetCascade(1); }}
                  getOptionLabel={(o) => o?.name || ''}
                  getOptionSelected={(o, v) => o._id === v._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={directoryLoading ? 'Loading…' : 'Pick a department'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {directoryLoading
                              ? <CircularProgress size={14} thickness={5} style={{ color: ORANGE }} />
                              : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <span className={classes.helper}>
                  Tech is not listed — tech work belongs in “ERP Tech Support Tickets”.
                </span>
              </div>
            </div>

            {pickedDepartment && (
              <div className={classes.pickerStep}>
                <span className={classes.pickerBadge} style={{ background: ORANGE }}>2</span>
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <label className={classes.label}>City *</label>
                  <Autocomplete
                    size="small"
                    options={cityOptions}
                    value={pickedCity}
                    onChange={(e, v) => { setPickedCity(v); resetCascade(2); }}
                    getOptionLabel={(o) => o?.name || ''}
                    getOptionSelected={(o, v) => o._id === v._id}
                    noOptionsText={`Nobody in ${pickedDepartment?.name || 'this department'} has a city set`}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Pick a city" />
                    )}
                  />
                  <span className={classes.helper}>
                    {`${cityOptions.length} ${cityOptions.length === 1 ? 'city' : 'cities'} with people in ${pickedDepartment?.name || 'this department'}`}
                  </span>
                </div>
              </div>
            )}

            {pickedDepartment && pickedCity && (
              <div className={classes.pickerStep}>
                <span className={classes.pickerBadge} style={{ background: ORANGE }}>3</span>
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <label className={classes.label}>Experience Center *</label>
                  <Autocomplete
                    size="small"
                    options={centreOptions}
                    value={pickedCentre}
                    onChange={(e, v) => { setPickedCentre(v); applyPickedUsers([]); }}
                    getOptionLabel={(o) => o?.name || ''}
                    getOptionSelected={(o, v) => o._id === v._id}
                    noOptionsText={`No centre in ${pickedCity?.name || 'this city'} has anyone from ${pickedDepartment?.name || 'this department'}`}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Pick an experience center" />
                    )}
                  />
                  <span className={classes.helper}>
                    {`${centreOptions.length} ${centreOptions.length === 1 ? 'centre' : 'centres'} in ${pickedCity?.name || 'this city'}`}
                  </span>
                </div>
              </div>
            )}

            {pickedCity && pickedCentre && (
              <div className={classes.pickerStep}>
                <span className={classes.pickerBadge} style={{ background: ORANGE }}>4</span>
                <div style={{ flexGrow: 1, minWidth: 0 }}>
                  <label className={classes.label}>Assign to *</label>
                  <Autocomplete
                    multiple
                    size="small"
                    options={userOptions}
                    value={pickedUsers}
                    onChange={(e, v) => applyPickedUsers(v)}
                    getOptionLabel={(o) => o?.name || o?.email || ''}
                    getOptionSelected={(o, v) => o._id === v._id}
                    noOptionsText="Nobody matches all four"
                    // Name, email and role on every row - picking a person by
                    // email alone means guessing whether m.kumar@ is the right
                    // M Kumar.
                    renderOption={(o) => (
                      <div className={classes.userRow}>
                        <span className={classes.userAvatar}>{initialsOf(o.name)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className={classes.userName}>{o.name || '—'}</div>
                          <div className={classes.userMeta}>{o.email}</div>
                          {roleNamesOf(o) && (
                            <div className={classes.userRole}>{roleNamesOf(o)}</div>
                          )}
                        </div>
                      </div>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={pickedUsers.length ? '' : 'Pick who this goes to'}
                      />
                    )}
                  />
                  <span className={classes.helper}>
                    {pickedUsers.length
                      ? `Assigning to ${pickedUsers.length} ${pickedUsers.length === 1 ? 'person' : 'people'} · ${userEmail}`
                      : `${userOptions.length} ${userOptions.length === 1 ? 'person' : 'people'} in ${pickedCentre?.name || 'this centre'}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <label htmlFor="ticketETAdate" className={classes.label}>Due date *</label>
              <TextField
                id="ticketETAdate"
                type="date"
                value={ticketETAdate}
                onChange={handleETAdateInputChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                // Greys out every past day in the calendar. The handler still
                // checks as well - `min` is advisory, a typed date goes
                // straight past it.
                inputProps={{ min: todayLocal() }}
              />
              <span className={classes.helper}>Today or later</span>
            </Grid>
          </Grid>
        </>
      )}

      {isTech && (
        <div className={classes.ownerCard}>
          <span className={classes.ownerAvatar}>L1</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{l1Name || 'L1 owner'}</span>
            <span style={{ fontSize: 12, color: MUTED }}>Primary owner · due date is driven by the L1 TAT</span>
          </div>
          {matchedEscalation?.l1User?.email && (
            <span className={classes.tatPill}>{matchedEscalation.l1User.email}</span>
          )}
        </div>
      )}

      <div className={classes.reviewBox}>
        <div className={classes.reviewHead}>
          <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <CheckRoundedIcon style={{ fontSize: 16, color: "#2E8B67" }} />
            Ready to create
          </span>
          <Button
            size="small"
            onClick={() => setActiveStep(2)}
            startIcon={<EditRoundedIcon style={{ fontSize: 14 }} />}
            style={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: NAVY, border: "1px solid #C9D3E4", borderRadius: 999, padding: "2px 14px" }}
          >
            Edit details
          </Button>
        </div>

        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div className={classes.titleCard}>
            <span className={classes.titleCardIcon}>
              <DescriptionRoundedIcon style={{ fontSize: 19 }} />
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#F0997B" }}>
                Ticket title
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#FFFFFF" }}>
                {ticketTitle || '—'}
              </span>
            </div>
          </div>

          <div className={classes.tileGrid}>
            <div className={classes.tile}>
              <span className={classes.tileLabel}>Category</span>
              <span className={classes.tileValue}>{selectedCategory || 'Not set'}</span>
            </div>
            <div className={classes.tile}>
              <span className={classes.tileLabel}>{isTech ? 'Query type' : 'Due date'}</span>
              <span className={classes.tileValue}>
                {isTech ? (selectedTechCategory || '—') : (ticketETAdate || '—')}
              </span>
            </div>
            <div className={classes.tile}>
              <span className={classes.tileLabel}>Attachments</span>
              <span className={classes.tileValue}>
                {files.filter((f) => f !== "").length} file(s)
              </span>
            </div>
          </div>
        </div>
      </div>

      {wrongEmail && (
        <span style={{ color: "#B3261E", fontSize: 13, fontWeight: 600 }}>
          {wrongMails} email is wrong, please try again
        </span>
      )}
    </>
  );

  return (
    <Fragment>
      <Paper className={classes.wrapper}>

        <div className={classes.header}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <span className={classes.headerTitle}>Create New Internal Ticket</span>
            <IconButton size="small" aria-label="Close" onClick={() => visibilityFunction(false)}>
              <CloseIcon style={{ color: "#FFFFFF" }} />
            </IconButton>
          </div>
          {renderStepper()}
        </div>

        {/* Reserved height, so the body does not jump when the bar appears. */}
        <div style={{ height: 3, flexShrink: 0 }}>
          {(submitting || uploadingIndex !== null) && <BrandLinearProgress />}
        </div>

        <div className={classes.body} ref={bodyRef}>
          <div className={classes.bodyInner}>
            {activeStep === 1 && renderStepOne()}
            {activeStep === 2 && renderStepTwo()}
            {activeStep === 3 && renderStepThree()}
          </div>
        </div>

        <div className={classes.footer}>
          <span className={classes.footerNote}>
            {activeStep === 3 && submitBlocker && !submitting ? (
              <span className={classes.blockerNote}>
                <InfoOutlinedIcon style={{ fontSize: 15 }} />
                {submitBlocker}
              </span>
            ) : submitting ? (
              <span className={classes.blockerNote} style={{ color: NAVY }}>
                <CircularProgress size={13} thickness={5} style={{ color: ORANGE }} />
                Creating the ticket and notifying the owner…
              </span>
            ) : uploadingIndex !== null ? (
              <span className={classes.blockerNote} style={{ color: NAVY }}>
                <CircularProgress size={13} thickness={5} style={{ color: ORANGE }} />
                Uploading your attachment…
              </span>
            ) : (
              <span style={{ fontSize: 13, fontWeight: 600, color: MUTED }}>
                Step {activeStep} of 3
              </span>
            )}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Button
              className={classes.ghostBtn}
              onClick={activeStep === 1 ? () => visibilityFunction(false) : goBack}
              startIcon={activeStep === 1 ? null : <ArrowBackRoundedIcon />}
              disabled={submitting}
            >
              {activeStep === 1 ? 'Cancel' : 'Back'}
            </Button>

            {activeStep < 3 ? (
              <Button
                className={classes.primaryBtn}
                onClick={goNext}
                disabled={uploadingIndex !== null}
                endIcon={<ArrowForwardRoundedIcon />}
              >
                Continue
              </Button>
            ) : (
              // The span is required: a disabled button fires no mouse events,
              // so the tooltip would never open on the one state it explains.
              <Tooltip title={submitBlocker || ''}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    className={classes.primaryBtn}
                    onClick={handleSubmitButton}
                    disabled={submitDisabled}
                    startIcon={submitting
                      ? <CircularProgress size={16} thickness={5} style={{ color: '#FFFFFF' }} />
                      : null}
                  >
                    {submitting ? 'Creating…' : 'Create Ticket'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div> 

      </Paper>
    </Fragment>
  );
};

 export default connect(null, { setSnackInfo })(CreateTaskTicket);